import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, CardHeader, EmptyState, Skeleton, Pill, Field, ATTENDANCE_TONES, REQUEST_TONES, fallbackTone } from "../../Components/ui";

// Sidebar items (Staff / Departments / Designations / Holidays) each have
// their own URL and render only their own panel — no tab strip. Holidays
// is its own top-level page at /holidays, not part of HRAdmin.
const VALID_PANELS = ["staff", "departments", "designations"];
const PANEL_TITLES = {
  staff:        { title: "Staff",        breadcrumb: "People & Org · Staff" },
  departments:  { title: "Departments",  breadcrumb: "People & Org · Departments" },
  designations: { title: "Designations", breadcrumb: "People & Org · Designations" },
};

export default function HRAdmin({ defaultTab }) {
  const role = localStorage.getItem("role");
  const panel = VALID_PANELS.includes(defaultTab) ? defaultTab : "staff";
  useShellHeader(PANEL_TITLES[panel], [panel]);

  if (role !== "SUPER_ADMIN") {
    return (
      <Card padding="lg">
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)" }}>Restricted</div>
        <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4 }}>
          HR module is available to administrators only.
        </div>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {panel === "staff"        && <StaffPanel />}
      {panel === "departments"  && <DepartmentsPanel />}
      {panel === "designations" && <DesignationsPanel />}
    </div>
  );
}

// ---------- Shared table primitives ----------
const tableStyle = { width: "100%", fontSize: 13, borderCollapse: "collapse" };
const thStyle = {
  textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const tdBase = { padding: "10px 12px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-secondary)" };

const btnPrimary = {
  background: "var(--accent-primary)", color: "white",
  padding: "6px 14px", border: "none", borderRadius: 8,
  fontSize: 13, fontWeight: 500, cursor: "pointer",
};
const btnGhost = {
  background: "transparent", color: "var(--text-secondary)",
  padding: "6px 12px", border: "1px solid var(--border-default)", borderRadius: 8,
  fontSize: 13, cursor: "pointer",
};
const btnDanger = {
  background: "transparent", color: "var(--tone-danger-fg)",
  padding: "4px 8px", border: "none", borderRadius: 6,
  fontSize: 12, cursor: "pointer",
};
const inputStyle = {
  border: "1px solid var(--border-default)", borderRadius: 8,
  padding: "6px 10px", fontSize: 13, background: "var(--bg-surface)",
  color: "var(--text-primary)", width: "100%",
};

// ============================================================
// DEPARTMENTS  — admin CRUD (port of admin/views/departments.php)
// ============================================================
function DepartmentsPanel() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", color: "" });
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const d = await api("/departments");
      setRows(Array.isArray(d) ? d : (d.departments || []));
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function create() {
    if (!form.name.trim()) { toast({ title: "Department name is required.", status: "warning" }); return; }
    try {
      await api("/departments", { method: "POST", body: JSON.stringify(form) });
      toast({ title: "Department added.", status: "success" });
      setForm({ name: "", color: "" });
      setShowForm(false);
      load();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this department? Staff assigned to it will lose the link.")) return;
    try {
      await api(`/departments/${id}`, { method: "DELETE" });
      toast({ title: "Deleted.", status: "info" });
      load();
    } catch (e) { toast({ title: e.data?.message || e.message, status: "error" }); }
  }

  return (
    <Card padding="none">
      <CardHeader
        title="Departments"
        action={
          <button style={btnPrimary} onClick={() => setShowForm((v) => !v)}>
            {showForm ? "Cancel" : "+ New department"}
          </button>
        }
      />
      {showForm && (
        <div style={{ padding: 16, borderBottom: "1px solid var(--border-subtle)", background: "var(--bg-subtle)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 10, alignItems: "end" }}>
            <Field label="Name"><input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Marketing" /></Field>
            <Field label="Color (optional)"><input style={inputStyle} value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="#E31E24" /></Field>
            <button style={btnPrimary} onClick={create}>Save</button>
          </div>
        </div>
      )}
      {loading ? (
        <div style={{ padding: 16 }}><Skeleton.Row count={3} /></div>
      ) : rows.length === 0 ? (
        <EmptyState icon="🏢" title="No departments yet" description="Create your first department above." />
      ) : (
        <table style={tableStyle}>
          <thead><tr>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Color</th>
            <th style={thStyle}>Created</th>
            <th style={{ ...thStyle, textAlign: "right" }}></th>
          </tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d._id || d.id}>
                <td style={{ ...tdBase, color: "var(--text-primary)", fontWeight: 500 }}>{d.name}</td>
                <td style={tdBase}>
                  {d.color ? (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: 4, background: d.color, border: "1px solid var(--border-subtle)" }} />
                      <span className="tabular" style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{d.color}</span>
                    </span>
                  ) : "—"}
                </td>
                <td className="tabular" style={tdBase}>{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : "—"}</td>
                <td style={{ ...tdBase, textAlign: "right" }}>
                  <button style={btnDanger} onClick={() => remove(d._id || d.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function DesignationsPanel() {
  const toast = useToast();
  const [designations, setDesignations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", team: "" });
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([api("/hr/designations"), api("/teams").catch(() => ({ teams: [] }))]);
      setDesignations(d.designations || []);
      setTeams(t.teams || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function create() {
    if (!form.name) { toast({ title: "Name required", status: "warning" }); return; }
    try {
      await api("/hr/designations", { method: "POST", body: JSON.stringify({ name: form.name, team: form.team || null }) });
      toast({ title: "Designation created", status: "success" });
      setForm({ name: "", team: "" });
      setShowForm(false);
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this designation? Staff currently assigned will be unset.")) return;
    try { await api(`/hr/designations/${id}`, { method: "DELETE" }); load(); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <Card padding="none">
      <CardHeader
        title="Designations"
        action={<button style={btnPrimary} onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "+ New designation"}</button>}
      />
      {showForm && (
        <div style={{ padding: 16, borderBottom: "1px solid var(--border-subtle)", background: "var(--bg-subtle)" }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 10, alignItems: "end" }}>
            <Field label="Name"><input style={inputStyle} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Senior Designer" /></Field>
            <Field label="Department">
              <select style={inputStyle} value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })}>
                <option value="">(any)</option>
                {teams.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
            </Field>
            <button style={btnPrimary} onClick={create}>Save</button>
          </div>
        </div>
      )}
      {loading ? (
        <div style={{ padding: 16 }}><Skeleton.Row count={4} /></div>
      ) : designations.length === 0 ? (
        <EmptyState icon="🏷️" title="No designations yet" />
      ) : (
        <table style={tableStyle}>
          <thead><tr><th style={thStyle}>Name</th><th style={thStyle}>Department</th><th style={{ ...thStyle, textAlign: "right" }}></th></tr></thead>
          <tbody>
            {designations.map((d) => (
              <tr key={d._id}>
                <td style={{ ...tdBase, color: "var(--text-primary)", fontWeight: 500 }}>{d.name}</td>
                <td style={tdBase}>{d.team?.name || "—"}</td>
                <td style={{ ...tdBase, textAlign: "right" }}>
                  <button style={btnDanger} onClick={() => remove(d._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ============================================================
// HOLIDAYS
// ============================================================
function StaffPanel() {
  const toast = useToast();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);          // user object or null
  const [profileId, setProfileId] = useState(null);      // staff _id for analytics modal
  const [shifts, setShifts] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [batchStats, setBatchStats] = useState({ att: {}, leaves: {} });
  const currentYear = new Date().getFullYear();

  async function load() {
    setLoading(true);
    try {
      const [s, sh, d] = await Promise.all([
        api("/hr/staff"),
        api("/hr/shifts"),
        api("/hr/designations"),
      ]);
      setStaff(s.staff || []);
      setShifts(sh.shifts || []);
      setDesignations(d.designations || []);
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Load attendance batch stats whenever staff list is ready
  useEffect(() => {
    if (!staff.length) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api(`/hr/staff-stats/batch?year=${currentYear}`);
        if (!cancel) setBatchStats({ att: r.att || {}, leaves: r.leaves || {} });
      } catch (e) { /* silent — chips just show "—" */ }
    })();
    return () => { cancel = true; };
  }, [staff, currentYear]);

  if (editing) {
    return <StaffEditor
      user={editing} shifts={shifts} designations={designations}
      onClose={() => setEditing(null)}
      onSaved={() => { setEditing(null); load(); }}
    />;
  }

  return (
    <>
      <Card padding="none">
        <CardHeader title="Staff" />
        {loading ? (
          <div style={{ padding: 16 }}><Skeleton.Row count={5} /></div>
        ) : staff.length === 0 ? (
          <EmptyState icon="👥" title="No staff yet" />
        ) : (
          <table style={tableStyle}>
            <thead><tr>
              <th style={thStyle}>Employee</th>
              <th style={thStyle}>Code</th>
              <th style={thStyle}>Department</th>
              <th style={thStyle}>Shift</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Attendance ({currentYear})</th>
              <th style={thStyle}>Joining</th>
              <th style={{ ...thStyle, textAlign: "right" }}></th>
            </tr></thead>
            <tbody>
              {staff.map((u) => (
                <tr key={u._id}>
                  <td style={{ ...tdBase }}>
                    <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                      {u.firstName || u.lastName ? `${u.firstName} ${u.lastName}`.trim() : u.username}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{u.email}</div>
                  </td>
                  <td className="tabular" style={tdBase}>{u.employeeCode || "—"}</td>
                  <td style={tdBase}>{u.team?.name || "—"}</td>
                  <td style={tdBase}>{u.shift ? `${u.shift.name} (${u.shift.startTime}–${u.shift.endTime})` : "—"}</td>
                  <td style={tdBase}>{u.employmentType?.replace("_", " ") || "—"}</td>
                  <td style={tdBase}>
                    <AttendanceChips
                      att={batchStats.att[u._id]}
                      lv={batchStats.leaves[u._id]}
                    />
                  </td>
                  <td className="tabular" style={tdBase}>{u.joiningDate ? new Date(u.joiningDate).toLocaleDateString() : "—"}</td>
                  <td style={{ ...tdBase, textAlign: "right", whiteSpace: "nowrap" }}>
                    <button style={{ ...btnGhost, padding: "4px 8px", marginRight: 4 }} title="View analytics" onClick={() => setProfileId(u._id)}>📊</button>
                    <button style={btnGhost} onClick={() => setEditing(u)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {profileId && (
        <StaffProfileModal
          staffId={profileId}
          onClose={() => setProfileId(null)}
        />
      )}
    </>
  );
}

// ============================================================
// ATTENDANCE CHIPS (rendered in the staff table row)
// ============================================================
const chipBase = {
  display: "inline-flex", alignItems: "center", padding: "2px 8px",
  borderRadius: 12, fontSize: 11, fontWeight: 600, lineHeight: 1.4,
  marginRight: 4, marginBottom: 2,
};
// Shared tones (Components/ui/statusTones.js). Note `late`: this map used the
// danger tint, while AttStatusPill below — in this same file — and every other
// page used warning. Unified on warning, so "Late" no longer reads as an error.
// Shared with the attendance and dashboard heroes: a fixed dark panel in both
// themes, brand red pushed into a corner glow. Labels on it are measured
// against black.
const HERO_BG = [
  "radial-gradient(120% 150% at 88% -10%, rgba(227,30,36,0.45) 0%, rgba(227,30,36,0.12) 40%, rgba(227,30,36,0) 72%)",
  "linear-gradient(135deg, #0A0A0B 0%, #000000 100%)",
].join(", ");

const chipPalette = {
  present: ATTENDANCE_TONES.present,
  late:    ATTENDANCE_TONES.late,
  wfh:     ATTENDANCE_TONES.wfh,
  leaves:  ATTENDANCE_TONES.leave,
};
function Chip({ tone, children }) {
  const p = chipPalette[tone] || chipPalette.present;
  return <span style={{ ...chipBase, background: p.bg, color: p.fg }}>{children}</span>;
}
function AttendanceChips({ att, lv }) {
  if (!att && !lv) return <span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>—</span>;
  const present   = (att && att.present)    || 0;
  const late      = (att && att.late)       || 0;
  const wfh       = (att && att.wfh)        || 0;
  const leaveDays = (lv && lv.leave_days)   || 0;
  const absent    = (att && att.absent)     || 0;
  if (!present && !late && !wfh && !leaveDays && !absent) {
    return <span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>No data</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap" }}>
      {present   ? <Chip tone="present">{present} Present</Chip> : null}
      {late      ? <Chip tone="late">{late} Late</Chip>          : null}
      {wfh       ? <Chip tone="wfh">{wfh} WFH</Chip>             : null}
      {leaveDays ? <Chip tone="leaves">{leaveDays} Leave</Chip>  : null}
      {!present && !late && !wfh && !leaveDays && absent ? <Chip tone="late">{absent} Absent</Chip> : null}
    </div>
  );
}

// ============================================================
// STAFF PROFILE ANALYTICS MODAL
// ============================================================
function StaffProfileModal({ staffId, onClose }) {
  const toast = useToast();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    api(`/hr/staff/${staffId}/profile-stats?year=${year}`)
      .then((r) => { if (!cancel) setData(r); })
      .catch((e) => { if (!cancel) toast({ title: e.message, status: "error" }); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [staffId, year]);

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const s       = (data && data.staff)   || {};
  const annual  = (data && data.annual)  || {};
  const monthly = (data && data.monthly) || [];
  const leaves  = (data && data.leaves)  || [];
  const recent  = (data && data.recent)  || [];
  const years   = (data && data.years)   || [year];

  const fullName = `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.username || "Staff";
  const initials = ((s.firstName || "").charAt(0) + (s.lastName || "").charAt(0)).toUpperCase() || "?";
  const meta     = [s.team?.name, s.designationRef?.name || s.designation].filter(Boolean).join(" · ");

  const totalDays = annual.total_days || 0;
  const good      = (annual.present || 0) + (annual.wfh || 0) + (annual.late || 0);
  const score     = totalDays > 0 ? Math.round((good / totalDays) * 100) : 0;
  const scoreColor = score >= 90 ? "var(--tone-success-fg)" : score >= 75 ? "var(--tone-warning-fg)" : "var(--tone-danger-fg)";

  const totalLeaveDays = leaves
    .filter((l) => l.status === "approved")
    .reduce((sum, l) => sum + (l.days || 0), 0);

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.55)",
        zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, animation: "spFadeIn 160ms ease",
      }}
    >
      <div style={{
        width: "min(960px, 100%)", maxHeight: "92vh", overflow: "auto",
        background: "var(--bg-surface)", borderRadius: 14, boxShadow: "var(--shadow-lg)",
        border: "1px solid var(--border-subtle)",
      }}>
        {/* Header — fixed dark panel with the brand red as a corner glow,
            matching the attendance and dashboard heroes. */}
        <div style={{
          display: "flex", alignItems: "center", gap: 16, padding: "20px 24px",
          background: HERO_BG,
          borderRadius: "14px 14px 0 0",
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%",
            background: "rgba(255,255,255,0.2)", color: "#fff",
            fontSize: 22, fontWeight: 700,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, border: "2px solid rgba(255,255,255,0.4)",
          }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, lineHeight: 1.15 }}>{fullName}</div>
            <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 13, marginTop: 2 }}>{meta || s.email || "—"}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, fontWeight: 600 }}>Year</label>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              style={{
                background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)",
                color: "#fff", padding: "5px 10px", borderRadius: 6, fontSize: 13,
              }}
            >
              {years.map((yr) => <option key={yr} value={yr} style={{ color: "#111" }}>{yr}</option>)}
            </select>
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                background: "rgba(255,255,255,0.15)", color: "#fff", border: "none",
                width: 32, height: 32, borderRadius: 8, cursor: "pointer", fontSize: 18, lineHeight: 1,
              }}
            >×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", opacity: loading ? 0.5 : 1, transition: "opacity 160ms" }}>
          {/* Metric cards */}
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(5, 1fr)",
            gap: 12, marginBottom: 20,
          }}>
            <MetricCard label="Present Days" value={annual.present || 0} color="var(--tone-success-fg)" sub={`${annual.late || 0} late`} />
            <MetricCard label="WFH Days"     value={annual.wfh     || 0} color="var(--tone-violet-fg)" />
            <MetricCard label="Leave Days"   value={totalLeaveDays}        color="var(--tone-warning-fg)" sub="approved" />
            <MetricCard label="Absent Days"  value={annual.absent  || 0} color="var(--tone-danger-fg)" />
            <MetricCard label="Avg Check-in" value={annual.avg_checkin || "—"} color="var(--tone-violet-fg)" sub="this year" small />
          </div>

          {/* Score bar */}
          <div style={{ marginBottom: 22 }}>
            <div style={{
              display: "flex", justifyContent: "space-between",
              fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 6,
            }}>
              <span>Attendance Score</span>
              <span style={{ color: scoreColor, fontWeight: 800 }}>{score}%</span>
            </div>
            <div style={{ height: 10, background: "var(--bg-subtle)", borderRadius: 10, overflow: "hidden" }}>
              <div style={{
                height: "100%", width: `${score}%`, background: scoreColor,
                borderRadius: 10, transition: "width 600ms ease",
              }} />
            </div>
          </div>

          {/* Monthly breakdown */}
          <Section title={`Monthly Breakdown — ${year}`}>
            {monthly.length === 0 ? (
              <div style={{ color: "var(--text-tertiary)", fontSize: 13, padding: "8px 0" }}>
                No attendance records for {year}.
              </div>
            ) : (
              <>
                <MonthlyBars monthly={monthly} />
                <Legend />
              </>
            )}
          </Section>

          {/* Leaves table */}
          <Section title={`Leave Summary — ${year}`}>
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Requests</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Days</th>
              </tr></thead>
              <tbody>
                {leaves.length === 0 ? (
                  <tr><td colSpan={4} style={{ ...tdBase, textAlign: "center", color: "var(--text-tertiary)" }}>
                    No leave records for {year}.
                  </td></tr>
                ) : leaves.map((l, i) => (
                  <tr key={i}>
                    <td style={{ ...tdBase, textTransform: "capitalize" }}>{l.leave_type}</td>
                    <td style={tdBase}><LeaveStatusPill status={l.status} /></td>
                    <td style={{ ...tdBase, textAlign: "center" }}>{l.requests}</td>
                    <td style={{ ...tdBase, textAlign: "center", fontWeight: 600 }}>{l.days} days</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* Recent records */}
          <Section title="Recent Attendance (Last 10)">
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Check-in</th>
                <th style={thStyle}>Check-out</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Note</th>
              </tr></thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...tdBase, textAlign: "center", color: "var(--text-tertiary)" }}>
                    No recent records.
                  </td></tr>
                ) : recent.map((r, i) => (
                  <tr key={i}>
                    <td style={tdBase}>{r.work_date}</td>
                    <td className="tabular" style={tdBase}>{r.check_in  || "—"}</td>
                    <td className="tabular" style={tdBase}>{r.check_out || "—"}</td>
                    <td style={tdBase}><AttStatusPill status={r.status} /></td>
                    <td style={{ ...tdBase, fontSize: 12, color: "var(--text-tertiary)" }}>{r.note || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        </div>
      </div>

      <style>{`@keyframes spFadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
    </div>
  );
}

function MetricCard({ label, value, color, sub, small }) {
  return (
    <div style={{
      background: "var(--bg-subtle)", border: "1px solid var(--border-subtle)",
      borderRadius: 10, padding: "14px 12px", textAlign: "center",
    }}>
      <div style={{
        fontSize: small ? 20 : 26, fontWeight: 800,
        color, lineHeight: 1, marginBottom: 4,
      }}>{value}</div>
      <div style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
        color: "var(--text-tertiary)", fontWeight: 600,
      }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{
        fontSize: 12, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.08em", color: "var(--text-tertiary)",
        marginBottom: 10, borderBottom: "1px solid var(--border-subtle)", paddingBottom: 6,
      }}>{title}</div>
      {children}
    </div>
  );
}

function MonthlyBars({ monthly }) {
  const max = Math.max(1, ...monthly.map((m) => m.total || 0));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {monthly.map((m) => {
        const pct = (n) => (m.total ? Math.round(((n || 0) / max) * 100) : 0);
        return (
          <div key={m.ym} style={{ display: "grid", gridTemplateColumns: "84px 1fr 110px", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>{m.label}</span>
            <div style={{ background: "var(--bg-subtle)", borderRadius: 4, height: 18, overflow: "hidden" }}>
              <div style={{ height: "100%", display: "flex" }}>
                {m.present ? <div style={{ width: `${pct(m.present)}%`, background: "var(--fill-success)" }} /> : null}
                {m.late    ? <div style={{ width: `${pct(m.late)}%`,    background: "var(--fill-warning)" }} /> : null}
                {m.wfh     ? <div style={{ width: `${pct(m.wfh)}%`,     background: "var(--fill-violet)" }} /> : null}
                {m.absent  ? <div style={{ width: `${pct(m.absent)}%`,  background: "var(--fill-danger)" }} /> : null}
              </div>
            </div>
            <span style={{ fontSize: 11, color: "var(--text-tertiary)", textAlign: "right", whiteSpace: "nowrap" }}>
              {m.present || 0}P&nbsp;{m.late || 0}L&nbsp;{m.wfh || 0}W&nbsp;{m.absent || 0}A
            </span>
          </div>
        );
      })}
    </div>
  );
}

function Legend() {
  const items = [
    { label: "Present", color: "var(--fill-success)" },
    { label: "Late",    color: "var(--fill-warning)" },
    { label: "WFH",     color: "var(--fill-violet)" },
    { label: "Absent",  color: "var(--fill-danger)" },
  ];
  return (
    <div style={{ display: "flex", gap: 14, marginTop: 10, fontSize: 11, color: "var(--text-tertiary)" }}>
      {items.map((it) => (
        <span key={it.label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: it.color }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function LeaveStatusPill({ status }) {
  const p = REQUEST_TONES[status] || REQUEST_TONES.pending;
  return <span style={{ background: p.bg, color: p.co, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>{status}</span>;
}
function AttStatusPill({ status }) {
  const p = ATTENDANCE_TONES[status] || fallbackTone;
  return <span style={{ background: p.bg, color: p.co, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>{status}</span>;
}

function StaffEditor({ user, shifts, designations, onClose, onSaved }) {
  const toast = useToast();
  const [u, setU] = useState({
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    employeeCode: user.employeeCode || "",
    employmentType: user.employmentType || "full_time",
    shift: user.shift?._id || "",
    designationRef: user.designationRef?._id || "",
    designation: user.designation || "",
    joiningDate: user.joiningDate ? user.joiningDate.slice(0, 10) : "",
    dob: user.dob ? String(user.dob).slice(0, 10) : "",
    gender: user.gender || "",
    bloodGroup: user.bloodGroup || "",
    salary: user.salary || 0,
    phone: user.phone || "",
    address: user.address || "",
    emergencyContact: user.emergencyContact || { name: "", relation: "", phone: "" },
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const payload = { ...u, shift: u.shift || null, designationRef: u.designationRef || null };
      await api(`/hr/staff/${user._id}`, { method: "PATCH", body: JSON.stringify(payload) });
      toast({ title: "Saved", status: "success" });
      onSaved();
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setSaving(false); }
  }

  return (
    <Card padding="lg">
      <CardHeader
        title={`Edit ${user.username || user.email}`}
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <button style={btnGhost} onClick={onClose}>Cancel</button>
            <button style={btnPrimary} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save"}</button>
          </div>
        }
      />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        <Field label="First name"><input style={inputStyle} value={u.firstName} onChange={(e) => setU({ ...u, firstName: e.target.value })} /></Field>
        <Field label="Last name"><input style={inputStyle} value={u.lastName} onChange={(e) => setU({ ...u, lastName: e.target.value })} /></Field>
        <Field label="Employee code"><input style={inputStyle} value={u.employeeCode} onChange={(e) => setU({ ...u, employeeCode: e.target.value })} /></Field>
        <Field label="Phone"><input style={inputStyle} value={u.phone} onChange={(e) => setU({ ...u, phone: e.target.value })} /></Field>
        <Field label="Joining date"><input type="date" style={inputStyle} value={u.joiningDate} onChange={(e) => setU({ ...u, joiningDate: e.target.value })} /></Field>
        <Field label="DOB"><input type="date" style={inputStyle} value={u.dob} onChange={(e) => setU({ ...u, dob: e.target.value })} /></Field>
        <Field label="Gender">
          <select style={inputStyle} value={u.gender} onChange={(e) => setU({ ...u, gender: e.target.value })}>
            <option value="">—</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_say">Prefer not to say</option>
          </select>
        </Field>
        <Field label="Blood group"><input style={inputStyle} value={u.bloodGroup} onChange={(e) => setU({ ...u, bloodGroup: e.target.value })} placeholder="e.g. O+" /></Field>
        <Field label="Employment type">
          <select style={inputStyle} value={u.employmentType} onChange={(e) => setU({ ...u, employmentType: e.target.value })}>
            <option value="full_time">Full-time</option>
            <option value="part_time">Part-time</option>
            <option value="intern">Intern</option>
            <option value="contract">Contract</option>
            <option value="consultant">Consultant</option>
          </select>
        </Field>
        <Field label="Designation">
          <select style={inputStyle} value={u.designationRef} onChange={(e) => setU({ ...u, designationRef: e.target.value })}>
            <option value="">—</option>
            {designations.map((d) => <option key={d._id} value={d._id}>{d.name}{d.team?.name ? ` (${d.team.name})` : ""}</option>)}
          </select>
        </Field>
        <Field label="Shift">
          <select style={inputStyle} value={u.shift} onChange={(e) => setU({ ...u, shift: e.target.value })}>
            <option value="">—</option>
            {shifts.map((s) => <option key={s._id} value={s._id}>{s.name} ({s.startTime}–{s.endTime})</option>)}
          </select>
        </Field>
        <Field label="Salary (₹/month)"><input type="number" min={0} style={inputStyle} value={u.salary} onChange={(e) => setU({ ...u, salary: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Address" full><input style={inputStyle} value={u.address} onChange={(e) => setU({ ...u, address: e.target.value })} /></Field>
        <Field label="Emergency contact name"><input style={inputStyle} value={u.emergencyContact?.name || ""} onChange={(e) => setU({ ...u, emergencyContact: { ...u.emergencyContact, name: e.target.value } })} /></Field>
        <Field label="Relation"><input style={inputStyle} value={u.emergencyContact?.relation || ""} onChange={(e) => setU({ ...u, emergencyContact: { ...u.emergencyContact, relation: e.target.value } })} /></Field>
        <Field label="Emergency phone"><input style={inputStyle} value={u.emergencyContact?.phone || ""} onChange={(e) => setU({ ...u, emergencyContact: { ...u.emergencyContact, phone: e.target.value } })} /></Field>
      </div>
    </Card>
  );
}

